import React from "react";
import EllipsisTooltip from "../components/EllipsisTooltip";
import { IArticleNode, IImageData, IMediaItem, IVideoData, MediaType } from "../models/interface";

// Cache for flattened articles to avoid repeated traversals
let flattenedCache: { articles: IArticleNode[]; flattened: IArticleNode[] } | null = null;

/**
 * Creates a Map for O(1) article lookup by key
 * This avoids expensive recursive searches
 */
export const createArticleLookupMap = (articles: IArticleNode[]): Map<string, IArticleNode> => {
	const map = new Map<string, IArticleNode>();

	const traverse = (items: IArticleNode[]) => {
		items.forEach((item) => {
			map.set(item.key, item);
			if (item.children?.length > 0) {
				traverse(item.children);
			}
		});
	};

	traverse(articles);
	return map;
};

/**
 * Optimized article lookup using pre-built map
 */
export const findArticleByKeyOptimized = (articleMap: Map<string, IArticleNode>, key: string): IArticleNode | undefined => {
	return articleMap.get(key);
};

/**
 * Legacy function - kept for backward compatibility
 * Consider migrating to map-based lookup
 * @deprecated Use createArticleLookupMap + findArticleByKeyOptimized instead
 */
export const findArticleByKey = (articles: IArticleNode[], key: string): IArticleNode | undefined => {
	for (const article of articles) {
		if (article.key === key) return article;
		if (article.children) {
			const found = findArticleByKey(article.children, key);
			if (found) return found;
		}
	}
};

export const findArticleById = (articles: IArticleNode[], id: string): IArticleNode | undefined => {
	for (const article of articles) {
		if (article.id === id) return article;
		if (article.children) {
			const found = findArticleById(article.children, id);
			if (found) return found;
		}
	}
};

export const findArticleByIdOrFirstWithContent = (articles: IArticleNode[], id: string): IArticleNode | undefined => {
	const found = findArticleById(articles, id);
	if (found) return found;

	const flattened = flattenArticles(articles);
	return flattened.find((article) => !!article.content);
};

export const renderMenuItems = (articles: IArticleNode[] | null | undefined) => {
	if (!articles || articles.length === 0) return [];
	return articles.map((article) => {
		const labelWithTooltip = (
			<EllipsisTooltip title={article?.label} placement="top">
				<span>{article?.label}</span>
			</EllipsisTooltip>
		);

		if (article?.children?.length > 0) {
			return {
				key: article?.key,
				label: labelWithTooltip,
				children: renderMenuItems(article?.children),
			};
		}

		return {
			key: article?.key,
			label: labelWithTooltip,
		};
	});
};

// Flatten the article tree into a sequential array
export const flattenArticles = (articles: IArticleNode[]): IArticleNode[] => {
	// Check cache
	if (flattenedCache && flattenedCache.articles === articles) {
		return flattenedCache.flattened;
	}

	const flattened: IArticleNode[] = [];

	const traverse = (items: IArticleNode[]) => {
		items.forEach((item) => {
			flattened.push(item);
			if (item.children && item.children.length > 0) {
				traverse(item.children);
			}
		});
	};

	traverse(articles);

	// Update cache
	flattenedCache = { articles, flattened };

	return flattened;
};

// Get the index of current article in the flattened list
export const getCurrentArticleIndex = (articles: IArticleNode[], currentKey: string | null): number => {
	if (!currentKey) return -1;
	const flattened = flattenArticles(articles);
	return flattened.findIndex((article) => article?.key === currentKey);
};

// Get previous article
export const getPreviousArticle = (articles: IArticleNode[], currentKey: string | null): IArticleNode | null => {
	if (!currentKey) return null;
	const flattened = flattenArticles(articles);
	const currentIndex = flattened.findIndex((article) => article?.key === currentKey);
	if (currentIndex <= 0) return null;
	return flattened[currentIndex - 1];
};

// Get next article
export const getNextArticle = (articles: IArticleNode[], currentKey: string | null): IArticleNode | null => {
	if (!currentKey) return null;
	const flattened = flattenArticles(articles);
	const currentIndex = flattened.findIndex((article) => article?.key === currentKey);
	if (currentIndex < 0 || currentIndex >= flattened.length - 1) return null;
	return flattened[currentIndex + 1];
};

// Check if there's a previous article
export const hasPreviousArticle = (articles: IArticleNode[], currentKey: string | null): boolean => {
	return getPreviousArticle(articles, currentKey) !== null;
};

// Check if there's a next article
export const hasNextArticle = (articles: IArticleNode[], currentKey: string | null): boolean => {
	return getNextArticle(articles, currentKey) !== null;
};

export const findNextArticleWithContent = (flattenedArticles: IArticleNode[], currentIndex: number): IArticleNode | null => {
	for (let i = currentIndex + 1; i < flattenedArticles.length; i++) {
		if (flattenedArticles[i].content) {
			return flattenedArticles[i];
		}
	}
	return null;
};

export const findPreviousArticleWithContent = (flattenedArticles: IArticleNode[], currentIndex: number): IArticleNode | null => {
	for (let i = currentIndex - 1; i >= 0; i--) {
		if (flattenedArticles[i].content) {
			return flattenedArticles[i];
		}
	}
	return null;
};

// Create a search index when articles load
export const createSearchIndex = (articles: IArticleNode[]) => {
	const index = new Map<string, { article: IArticleNode; searchableText: string }>();
	const traverse = (items: IArticleNode[]) => {
		items.forEach((item) => {
			const contentText = item.content?.replace(/<[^>]*>/g, " ").toLowerCase() || "";
			const labelText = item.label?.toLowerCase() || "";
			index.set(item.key, {
				article: item,
				searchableText: `${labelText} ${contentText}`,
			});
			if (item.children?.length) {
				traverse(item.children);
			}
		});
	};
	traverse(articles);
	return index;
};

export const searchArticles = (articles: IArticleNode[] | null | undefined, searchText: string): any[] => {
	if (!articles || !searchText?.trim()) return [];
	const results: any[] = [];
	const lowerSearchText = searchText.toLowerCase();
	const searchRecursive = (items: IArticleNode[], parentPath: string[] = []) => {
		items?.forEach((item) => {
			// Search in label
			const labelMatch = item?.label?.toLowerCase().includes(lowerSearchText);
			// Search in content (strip HTML tags first)
			const contentText = item?.content?.replace(/<[^>]*>/g, " ").toLowerCase() || "";
			const contentMatch = contentText.includes(lowerSearchText);

			if (labelMatch || contentMatch) {
				results.push({
					...item,
					parentPath,
					matchType: labelMatch ? "label" : "content",
				});
			}
			if (item.children && item?.children?.length > 0) {
				searchRecursive(item.children, [...parentPath, item.label]);
			}
		});
	};
	searchRecursive(articles);
	return results;
};

// Function to find all parent keys for a given article key
export const findParentKeys = (articles: IArticleNode[], targetKey: string, parents: string[] = []): string[] => {
	for (const article of articles) {
		if (article.key === targetKey) {
			return parents;
		}
		if (article.children?.length > 0) {
			const found = findParentKeys(article.children, targetKey, [...parents, article.key]);

			if (found.length > 0 || article.children.some((child) => child.key === targetKey)) {
				// return [...parents, article.key];
				return found;
			}
		}
	}
	return [];
};

export const getArticleToSelect = (article: IArticleNode): IArticleNode | null => {
	if (!article) return null;

	// Article has content - return it
	if (article.content) {
		return article;
	}

	// No content but has children - return first child with content
	if (article.children?.length) {
		let targetArticle: IArticleNode | undefined = article.children[0];

		// Keep looking for an article with content
		while (targetArticle && !targetArticle.content) {
			if (targetArticle.children?.length > 0) {
				targetArticle = targetArticle.children[0];
			} else {
				break;
			}
		}

		return targetArticle || article.children[0];
	}

	// Fallback - return the article anyway
	return article;
};

/**
 * Validates if a search query is valid
 */
export const isValidSearchQuery = (query: string): boolean => {
	return query?.trim().length > 0;
};

export const generateBreadcrumbItems = (article: IArticleNode | null) => {
	if (!article?.id) return [];
	const parts = article.id.split("___");
	return parts.map((part, index) => {
		const formattedPart = part
			.split("-")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");

		return {
			title: formattedPart,
		};
	});
};

export const CACHE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

// Helper to check if string is valid and non-empty
export const isValidUrl = (url?: string): url is string => {
	return Boolean(url && url.trim());
};

// Transform image item to image data
export const transformImageData = (item: IMediaItem): IImageData | null => {
	if (item.type === MediaType.IMG && isValidUrl(item.img_url)) {
		return {
			id: item.id,
			url: item.img_url,
		};
	}
	return null;
};

// Transform video item to video data
export const transformVideoData = (item: IMediaItem): IVideoData | null => {
	if (item.type === MediaType.VID && isValidUrl(item.video_url)) {
		const videoData: IVideoData = {
			id: item.id,
			url: item.video_url,
		};

		// Add optional URLs only if valid
		if (isValidUrl(item.subtitle_url)) videoData.subtitle_url = item.subtitle_url;
		if (isValidUrl(item.chapters_url)) videoData.chapters_url = item.chapters_url;
		if (isValidUrl(item.thumbnail_url)) videoData.thumbnail_url = item.thumbnail_url;

		return videoData;
	}
	return null;
};

// Create empty cache structure
export const createEmptyCache = (timestamp: number) => ({
	images: [],
	videos: [],
	fetchedAt: timestamp,
	expiresAt: timestamp + CACHE_TTL_MS,
});
